import {inject, Injectable} from '@core/di';
import {AppInitializeService} from '@core/app-initialize-service';
import {UiService} from '@core/view-engine/ui-service';
import {ConfigService} from '@core/config/config-service';
import {Logger} from '@core/logger';
import {I18nService} from '@core/i18n/i18n-service';
import {TemplatesService} from '@core/view-engine/templates-service';
import {TimeService} from '@core/time-service';
import timePickerTemplate from './time-picker.html';
import './time-picker.scss';
import moment from 'moment-timezone';

@Injectable()
export class TimePicker implements AppInitializeService {
  private readonly configService = inject(ConfigService);
  private readonly logger = inject(Logger);
  private readonly uiService = inject(UiService);
  private readonly i18nService = inject(I18nService);
  private readonly templatesService = inject(TemplatesService);
  private readonly timeService = inject(TimeService);

  /**
   * Default time offset in minutes between current time and planned delivery time.
   */
  private readonly DEFAULT_MIN_TIME_OFFSET = 5;

  /**
   * Minimal time offset between current time and planned delivery time. In minutes.
   */
  private minTimeOffset: number = this.DEFAULT_MIN_TIME_OFFSET;

  /**
   * Current timepicker value as MomentJS object
   */
  public moment: moment.Moment | undefined = undefined;

  constructor() {
    window.time_picker = this;
  }

  public init() {
    this.templatesService.addTemplate('time_picker', timePickerTemplate);

    this.uiService.renderPageOnTheStructv2(
      'time_picker',
      {
        title: this.i18nService.translate('select_time_date'),
      },
      'time_picker',
      'time_picker new',
    );
    return true;
  }

  /**
   * Open time picker
   * @param parent - where to return to when closing time picker [{$id|menu}]
   * @param callback - callback executed when confirming time
   * @param timeOffset - Time offset (in minutes) relative to the current time; specifies the earliest time for which the order can be placed.
   * @param onOpen - open time picker callback
   * @param onClose - close time picker callback
   */
  public open(parent: string, callback: string, timeOffset?: number, onOpen?: string, onClose?: string) {
    const config = this.configService.config;
    this.minTimeOffset = timeOffset || config.time_picker?.minShift || this.DEFAULT_MIN_TIME_OFFSET;

    this.moment = this.timeService.now.add(this.minTimeOffset, 'm');
    this.setCurrentData(parent, callback, onOpen, onClose);

    window.navigate('#time_picker', null, true);
  }

  public close() {
    window.tv_keydown_override = null;
    this.moment = undefined;
    this.minTimeOffset = this.DEFAULT_MIN_TIME_OFFSET;

    $('.time_picker').find('.time_picker_item, .button').removeClass('tv_sel tv_cur');
  }

  /**
   * Increase or decrease one of the inputs of datepicker
   * @param direct time slider move direction. `up` - decrease time, `down` - increase time.
   */
  public move(direct: 'up' | 'down') {
    if (window.tv_cur_elem && window.tv_cur_elem.hasClass('button')) {
      // If selected element is not one of the inputs - force select the first selectable element
      window.tv_cur_pos = 0;
      window.tv_sel_cur();
      return;
    }

    this.processInputChange(direct);
  }

  public keyDownOverride(e: KeyboardEvent) {
    const code = window.getKeyCode(e);

    switch (code) {
      case window.tv_keys.INPUT:
        this.close();
        window.navigate('#sources_page');
        break;

      case window.tv_keys.UP:
        this.move('down');
        break;
      case window.tv_keys.DOWN:
        this.move('up');
        break;
      case window.tv_keys.LEFT:
        window.tv_left();
        break;
      case window.tv_keys.RIGHT:
        window.tv_right();
        break;
      case window.tv_keys.ENTER:
        if (window.tv_cur_elem && window.tv_cur_elem.hasClass('button') && window.tv_cur_elem.hasClass('disabled')) {
          return;
        }
        if (window.tv_cur_block === 'dialog') {
          window.tv_ok();
          return;
        }

        if (window.tv_cur_elem.hasClass('button')) {
          if (this.isNotValid()) {
            return;
          }

          window.tv_ok();
          this.close();
        }
        // нажимая Enter на выбиралке смещаем курсор вправо
        else {
          window.tv_right();
        }

        break;
      case window.tv_keys.EXIT:
      case window.tv_keys.BACK:
        if (window.tv_cur_block === 'dialog') {
          window.tv_ok();
          return;
        }

        this.close();

        window.tv_back();
        break;

      case window.tv_keys.RED:
        this.close();
        window.tv_back();
        window.tv_mode();
        break;
      case window.tv_keys.GREEN:
        if (window.fullscreen && !window.tv_channellist_hidden) {
          window.tv_channel_category_build();
        } else {
          //tv_log('не буду');
        }
        break;
      case window.tv_keys.BLUE:
        this.close();
        window.navigate('#language_select');
        break;

      case window.tv_keys.PORTAL:
      case window.tv_keys.GUIDE:
      case window.tv_keys.Q_MENU:
      case window.tv_keys.MENU:
      case window.tv_keys.HOME:
        this.close();
        window.navigate('#menu');
        break;

      default:
        //tv_log('code ' + code);
        break;
    }
  }

  private getMomentFromPicker(direct?: 'up' | 'down', type?: TimePickerInputType) {
    const hoursStr = $('[data-type="hours"]')
      .find('li')
      .eq(type === 'hours' ? (direct === 'up' ? 0 : 2) : 1)
      .data('item') as string;
    const minutesStr = $('[data-type="minutes"]')
      .find('li')
      .eq(type === 'minutes' ? (direct === 'up' ? 0 : 2) : 1)
      .data('item') as string;
    const daysStr = $('[data-type="days"]')
      .find('li')
      .eq(type === 'days' ? (direct === 'up' ? 0 : 2) : 1)
      .data('item') as string;

    const hours = moment.tz(hoursStr, this.timeService.timezone);
    const minutes = moment.tz(minutesStr, this.timeService.timezone);
    const days = moment.tz(daysStr, this.timeService.timezone);

    return this.timeService.now.set({
      year: days.get('year'),
      month: days.get('month'),
      date: days.get('date'),
      hour: hours.get('hours'),
      minute: minutes.get('minutes'),
      second: 0,
    });
  }

  private setTime() {
    this.moment = this.getMomentFromPicker();
    const time = this.moment.locale('en').format('YYYY-MM-DD HH:mm:00');
    $('.time_picker .button').attr('time', time);
  }

  private isNotValid() {
    const pickerTime = this.getMomentFromPicker();
    const currentTime = this.timeService.now.add(this.minTimeOffset - 1, 'm');
    return pickerTime.diff(currentTime, 'minutes') < 0;
  }

  private setCurrentData(parent: string, callback: string, onOpen?: string, onClose?: string) {
    const timePickerBlock = $('#time_picker');

    let oncloseStr = 'time_picker.close(); ';
    if (onClose) {
      oncloseStr += onClose;
    }

    timePickerBlock.attr('onopen', onOpen ? onOpen : '');
    timePickerBlock.attr('onclose', oncloseStr);

    this.setImposition(timePickerBlock, parent, callback);
    this.setPickerTime(timePickerBlock);

    this.setTime();
  }

  private setImposition(timePickerBlock: JQuery<HTMLElement>, parent: string, callback: string) {
    timePickerBlock.find('.back').remove();
    timePickerBlock.find('.menu').remove();

    const backLabel = this.i18nService.translate('back');
    if (parent) {
      parent = parent.startsWith('#') ? parent : '#' + parent;
      timePickerBlock.find('.header').append(`<div class="back" href="${parent}" href_type="back">${backLabel}</div>`);
    } else {
      timePickerBlock.find('.header').append(`<div class="menu" onvclick="show_menu()">${backLabel}</div>`);
    }

    if (callback) {
      timePickerBlock.find('.button').attr('onvclick', callback);
    } else {
      this.logger.warn('[TimePicker] Please add callback parameter to timePicker.open function call');
    }
  }

  private setPickerTime(timePickerBlock: JQuery<HTMLElement>) {
    const minutes = this.moment!.minutes();
    const sub = minutes % 5;

    // округляем до ближайших 5 минут
    if (sub) {
      if (sub > 2) {
        this.moment!.add(5 - sub, 'm').minutes();
      } else {
        this.moment!.subtract(sub, 'm').minutes();
      }
    }

    timePickerBlock.find('li').remove();
    this.setTimeItems('minutes', timePickerBlock);
    this.setTimeItems('hours', timePickerBlock);
    this.setTimeItems('days', timePickerBlock);
  }

  private setTimeItems(type: TimePickerInputType, timePickerBlock: JQuery<HTMLElement>) {
    const shift = type === 'minutes' ? 5 : 1;
    const labelFormat = type === 'minutes' ? 'mm' : type === 'hours' ? 'HH' : 'll';

    const prev = this.moment!.clone().subtract(shift, type);
    const cur = this.moment!.clone();
    const next = this.moment!.clone().add(shift, type);

    timePickerBlock.find(`[data-type="${type}"] ul`).append(
      `
<li data-item="${prev.toISOString()}" class="color-attention">${prev.format(labelFormat)}</li>
<li data-item="${cur.toISOString()}">${cur.format(labelFormat)}</li>
<li data-item="${next.toISOString()}">${next.format(labelFormat)}</li>
`,
    );
  }

  /**
   * Process datepicker input change
   * @param direct
   */
  private processInputChange(direct: 'up' | 'down') {
    const type = window.tv_cur_elem.data('type') as TimePickerInputType;

    // We hold only 3 elements in memory - previous, current and next.
    // Deciding which element should be removed, and which to make as current
    const whichDelete = direct === 'down' ? 'first' : 'last';
    const whichNext = direct !== 'down' ? 'first' : 'last';
    const container = $(`[data-type="${type}"] ul`);
    const containerMinutes = $('[data-type="minutes"] ul').find(`li:${whichNext}`);
    const containerHours = $('[data-type="hours"] ul').find(`li:${whichNext}`);
    const containerDays = $('[data-type="days"] ul').find(`li:${whichNext}`);

    if (container.hasClass('animation')) {
      return;
    }
    container.addClass('animation');

    this.addItem(direct, type, container, containerMinutes, containerHours, containerDays);
    this.removeItem(direct, whichDelete, container);
  }

  private addItem(
    direct: 'up' | 'down',
    type: TimePickerInputType,
    container: JQuery<HTMLElement>,
    containerMinutes: JQuery<HTMLElement>,
    containerHours: JQuery<HTMLElement>,
    containerDays: JQuery<HTMLElement>,
  ) {
    const badTime = this.isInvalidTime(direct, type);
    if (badTime) {
      switch (type) {
        case 'minutes':
          containerMinutes.addClass('color-attention');
          break;
        case 'hours':
          containerHours.addClass('color-attention');
          break;
        case 'days':
          containerDays.addClass('color-attention');
          break;
      }
      $('.time_picker .button').addClass('disabled');
    } else {
      $('.time_picker .button').removeClass('disabled');
    }

    const momentRelative = moment.tz(container.find('li:eq(1)').data('item') as string, this.timeService.timezone);
    const shift = type === 'minutes' ? 10 : 2;
    const labelFormat = type === 'minutes' ? 'mm' : type === 'hours' ? 'HH' : 'll';
    const momentWithShift =
      direct === 'up' ? momentRelative.clone().subtract(shift, type) : momentRelative.clone().add(shift, type);

    const label = momentWithShift.format(labelFormat);
    const itemData = momentWithShift.toISOString();
    const $element = $(`<li style="height: 0;" data-item="${itemData}">${label}</li>`);

    if (direct === 'up') {
      container.prepend($element);
    } else {
      container.append($element);
    }

    $element.animate({height: 100}, 300, () => {
      if (direct === 'up') {
        container.removeClass('animation');
      }

      if (!badTime) {
        $('[data-type="minutes"] ul > li').removeClass('color-attention');
        $('[data-type="hours"] ul > li').removeClass('color-attention');
        $('[data-type="days"] ul > li').removeClass('color-attention');
        // $('.time_picker .button').removeClass('disabled');
      }

    });
  }

  private isInvalidTime(direct: 'up' | 'down', type: TimePickerInputType): boolean {
    const newTime = this.getMomentFromPicker(direct, type);
    const currentTime = this.timeService.now.add(this.minTimeOffset - 1, 'm');
    return newTime.diff(currentTime, 'minutes') < 0;
  }

  private removeItem(direct: 'up' | 'down', whichDelete: 'first' | 'last', container: JQuery<HTMLElement>) {
    const animatedElement = window.tv_cur_elem.find(`li:${whichDelete}`);
    animatedElement.animate({height: 0}, 300, () => {
      animatedElement.remove();
      if (direct === 'down') {
        container.removeClass('animation');
      }
      this.setTime();
    });
  }
}

declare global {
  interface Window {
    time_picker: TimePicker;
  }
  const time_picker: TimePicker;
}

/**
 * Type of datepicker input. Should be compatible with Moment JS units.
 */
type TimePickerInputType = 'hours' | 'minutes' | 'days';
